import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardPathForUser } from '../lib/auth-routing';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Shield, 
  BarChart3, 
  Users, 
  FileSearch,
  ArrowRight,
  CheckCircle2,
  Building2,
  UserPlus,
  Mail,
  Phone,
  MessageSquare,
  Briefcase,
  Send,
  Loader2,
  Radar,
  Target,
  TrendingUp,
  Zap,
  Bell,
  Search,
  Award,
  ArrowRightCircle,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { LEGAL_PAGES_HIDDEN } from '../lib/version';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const dashboardPath = getDashboardPathForUser(user);
  const displayName =
    user?.first_name
      ? `${user.first_name}${user.last_name ? ` ${user.last_name}` : ''}`
      : (user?.email?.split('@')[0] || 'Account');
  const initials = (() => {
    if (!user) return '?';
    if (user.first_name) {
      return `${user.first_name[0]}${user.last_name ? user.last_name[0] : ''}`.toUpperCase();
    }
    return (user.email || '?').slice(0, 2).toUpperCase();
  })();
  const [showGetStartedModal, setShowGetStartedModal] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    lead_type: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInviteSubmit = () => {
    if (inviteToken.trim()) {
      navigate(`/invite/${inviteToken.trim()}`);
    }
  };

  const updateContactForm = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      const data = await response.json();
      setSubmitSuccess(true);
      toast.success(data.message || 'Thank you! We\'ll be in touch soon.');
      
      // Reset form after success
      setContactForm({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        lead_type: 'general'
      });
      
      // Reset success state after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmitContact = contactForm.name && contactForm.email && contactForm.message.length >= 10;

  const features = [
    {
      icon: Radar,
      title: 'SAM.gov Sentinel',
      description: 'AI-powered opportunity scouting that matches contracts to your NAICS/PSC codes',
    },
    {
      icon: FileSearch,
      title: 'RFP Analysis',
      description: 'Deep-dive analysis of RFP packages against your capabilities',
    },
    {
      icon: BarChart3,
      title: 'pWin Scoring',
      description: 'Risk-adjusted probability of win calculations',
    },
    {
      icon: Shield,
      title: 'Gap Identification',
      description: 'Identify missing certifications and competitive gaps',
    },
  ];

  const benefits = [
    'Discover new opportunities automatically with SAM.gov Sentinel',
    'Reduce bid/no-bid decision time by 60%',
    'Identify capability gaps before submission',
    'Track competitive intelligence across pursuits',
    'Collaborate securely with external consultants',
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation - outside main for semantic correctness */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-700 rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-sm">F</span>
              </div>
              <span className="font-semibold text-slate-900 text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                FedBD
              </span>
            </div>
            <div className="flex items-center gap-4">
              {isAuthenticated && user ? (
                <>
                  {/* Soft redirect — stay on the marketing page but offer a
                      one-click jump back into the app. Keep the avatar +
                      sign-out as a secondary affordance so users can switch
                      accounts without going to /login first. */}
                  <Button
                    onClick={() => navigate(dashboardPath)}
                    data-testid="nav-dashboard-btn"
                    className="bg-teal-700 hover:bg-teal-800 text-white btn-lift"
                    aria-label="Go to your dashboard"
                  >
                    Go to Dashboard
                    <ArrowRightCircle className="w-4 h-4 ml-1.5" />
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="nav-account-trigger"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 text-slate-700 font-semibold text-sm border border-slate-200 hover:border-teal-400 transition-colors"
                        aria-label="Account menu"
                      >
                        {initials}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="text-xs">
                        <div className="font-semibold text-slate-900">{displayName}</div>
                        <div className="text-slate-500 truncate">{user.email}</div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate(dashboardPath)}
                        data-testid="nav-account-dashboard"
                      >
                        <ArrowRightCircle className="w-4 h-4 mr-2" />
                        Go to Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer text-rose-600 focus:text-rose-700"
                        onClick={() => {
                          if (logout) logout();
                        }}
                        data-testid="nav-account-signout"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    data-testid="nav-login-btn"
                    className="text-slate-600 hover:text-slate-900"
                    aria-label="Sign in to FedBD"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => setShowGetStartedModal(true)}
                    data-testid="nav-get-started-btn"
                    className="bg-teal-700 hover:bg-teal-800 text-white btn-lift"
                    aria-label="Get started with FedBD"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main>
      {/* Hero Section */}
      <section id="features" className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Background gradient overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.03) 0%, rgba(15, 118, 110, 0.05) 100%)',
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700 font-medium">
                  <Radar className="w-4 h-4" />
                  Federal BD Enablement
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Find, Analyze &
                  <span className="block text-teal-700">Win Federal Contracts</span>
                </h1>
                
                <p className="text-base lg:text-lg text-slate-600 max-w-xl leading-relaxed">
                  From lead generation to bid decisions — FedBD's AI-powered platform scouts SAM.gov opportunities, 
                  analyzes RFPs against your capabilities, and helps you make smarter Go/No-Go decisions.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                {isAuthenticated && user ? (
                  <Button
                    size="lg"
                    onClick={() => navigate(dashboardPath)}
                    data-testid="hero-dashboard-btn"
                    className="bg-teal-700 hover:bg-teal-800 text-white btn-lift text-base px-8 py-6"
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    onClick={() => setShowGetStartedModal(true)}
                    data-testid="hero-get-started-btn"
                    className="bg-teal-700 hover:bg-teal-800 text-white btn-lift text-base px-8 py-6"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                )}
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-learn-more-btn"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 text-base px-8 py-6"
                >
                  Learn More
                </Button>
              </div>

              {/* Benefits list */}
              <div className="pt-4 space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side - Feature cards */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="stagger-item bg-white p-5 rounded-lg border border-slate-200 shadow-sm card-hover"
                  >
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-3">
                      <feature.icon className="w-5 h-5 text-teal-700" />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {feature.title}
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700 font-medium mb-4">
              <Zap className="w-4 h-4" />
              How It Works
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              From SAM.gov Discovery to
              <span className="block text-teal-700">Federal Contract Award</span>
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              FedBD streamlines your entire federal capture lifecycle with AI-powered tools — from automated opportunity scouting to pWin-driven Go/No-Go decisions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1: Find */}
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border border-teal-100 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Find Opportunities
                  </h3>
                </div>
                <div className="flex-1 grid grid-rows-3 gap-4">
                  <div className="flex items-start gap-3">
                    <Radar className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">SAM.gov Sentinel</p>
                      <p className="text-xs text-slate-500 line-clamp-2">Automated scanning matches opportunities to your NAICS/PSC codes</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Bell className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Smart Alerts</p>
                      <p className="text-xs text-slate-500 line-clamp-2">Get notified when high-value contracts match your profile</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Tiered Matching</p>
                      <p className="text-xs text-slate-500 line-clamp-2">Gold, Silver, Bronze scores help you prioritize pursuits</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Connector arrow */}
              <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-8 h-8 text-teal-300" />
              </div>
            </div>

            {/* Step 2: Analyze */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Analyze Fit
                  </h3>
                </div>
                <div className="flex-1 grid grid-rows-3 gap-4">
                  <div className="flex items-start gap-3">
                    <FileSearch className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">RFP Shredder</p>
                      <p className="text-xs text-slate-500 line-clamp-2">AI extracts requirements from complex solicitation documents</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">pWin Scoring</p>
                      <p className="text-xs text-slate-500 line-clamp-2">Data-driven probability of win calculations for Go/No-Go</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Search className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Gap Analysis</p>
                      <p className="text-xs text-slate-500 line-clamp-2">Identify missing certs, past performance, and personnel</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Connector arrow */}
              <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-8 h-8 text-blue-300" />
              </div>
            </div>

            {/* Step 3: Win */}
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl border border-emerald-100 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Win Contracts
                  </h3>
                </div>
                <div className="flex-1 grid grid-rows-3 gap-4">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Go/No-Go Decisions</p>
                      <p className="text-xs text-slate-500 line-clamp-2">AI-powered recommendations backed by deep analysis</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Team Collaboration</p>
                      <p className="text-xs text-slate-500 line-clamp-2">Bring in SMEs and reviewers for red team reviews</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Teaming Suggestions</p>
                      <p className="text-xs text-slate-500 line-clamp-2">AI recommends partners to fill capability gaps</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left side - Info */}
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700 font-medium mb-4">
                  <MessageSquare className="w-4 h-4" />
                  Get in Touch
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Federal Contracting Support for
                  <span className="block text-teal-700">Contractors & Consultants</span>
                </h2>
                <p className="text-base text-slate-600 leading-relaxed">
                  Whether you're a contractor looking for bid intelligence or a consultant wanting to partner with us, we'd love to hear from you.
                </p>
              </div>

              {/* Consultant CTA */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Are You a Consultant?
                    </h3>
                    <p className="text-slate-300 text-sm mb-4">
                      Partner with FedBD to offer your clients cutting-edge bid intelligence and analysis tools. Select "Consultant Inquiry" in the form to learn about our partnership program.
                    </p>
                    <div className="flex items-center gap-2 text-teal-400 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>White-label solutions available</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Value Props */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">24-Hour Response</p>
                    <p className="text-xs text-slate-500">We respond to every inquiry within one business day.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">No Commitment</p>
                    <p className="text-xs text-slate-500">Free initial consultation — no strings attached.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">Trusted by Contractors</p>
                    <p className="text-xs text-slate-500">Supporting federal contractors across all set-aside categories.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              {submitSuccess ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Message Sent!
                  </h3>
                  <p className="text-slate-600">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="contact-type">I am a...</Label>
                    <Select 
                      value={contactForm.lead_type} 
                      onValueChange={(v) => updateContactForm('lead_type', v)}
                    >
                      <SelectTrigger className="border-slate-200 bg-white" data-testid="contact-type-select">
                        <SelectValue placeholder="Select inquiry type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Federal Contractor (General Inquiry)</SelectItem>
                        <SelectItem value="consultant">Consultant (Partnership Inquiry)</SelectItem>
                        <SelectItem value="investor">Investor Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Full Name *</Label>
                      <Input
                        id="contact-name"
                        placeholder="John Smith"
                        value={contactForm.name}
                        onChange={(e) => updateContactForm('name', e.target.value)}
                        className="border-slate-200 bg-white"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="john@company.com"
                        value={contactForm.email}
                        onChange={(e) => updateContactForm('email', e.target.value)}
                        className="border-slate-200 bg-white"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-company">Company</Label>
                      <Input
                        id="contact-company"
                        placeholder="Acme Corp"
                        value={contactForm.company}
                        onChange={(e) => updateContactForm('company', e.target.value)}
                        className="border-slate-200 bg-white"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone">Phone</Label>
                      <Input
                        id="contact-phone"
                        placeholder="+1 (555) 123-4567"
                        value={contactForm.phone}
                        onChange={(e) => updateContactForm('phone', e.target.value)}
                        className="border-slate-200 bg-white"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      placeholder={contactForm.lead_type === 'consultant' 
                        ? "Tell us about your consultancy and how you'd like to partner with FedBD..."
                        : "Tell us about your contracting needs and how we can help..."
                      }
                      value={contactForm.message}
                      onChange={(e) => updateContactForm('message', e.target.value)}
                      className="border-slate-200 bg-white min-h-[120px] resize-none"
                      data-testid="contact-message-input"
                    />
                    <p className="text-xs text-slate-500">Minimum 10 characters</p>
                  </div>

                  <Button
                    type="submit"
                    disabled={!canSubmitContact || isSubmitting}
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white py-6"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to our{' '}
                    <a href="/privacy" className="text-teal-700 hover:underline" rel="nofollow">Privacy Policy</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section — AIEO: Structured Q&A for AI answer engines */}
      <section id="faq" className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-base text-slate-600">
              Common questions about FedBD's federal BD enablement platform.
            </p>
          </div>

          <div className="space-y-6" data-testid="faq-section">
            <details className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="faq-item-what-is-fedbd">
              <summary className="flex items-center justify-between cursor-pointer p-6 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                What is FedBD?
                <ArrowRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
              </summary>
              <div className="px-6 pt-4 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                FedBD is an AI-powered federal BD enablement platform built for government contractors and BD consultants. It automates SAM.gov opportunity scouting, analyzes RFP documents against your capabilities, calculates probability of win (pWin), and helps you make smarter Go/No-Go bid decisions — all from a single dashboard.
              </div>
            </details>

            <details className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="faq-item-pwin">
              <summary className="flex items-center justify-between cursor-pointer p-6 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                What is pWin scoring and how does it work?
                <ArrowRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
              </summary>
              <div className="px-6 pt-4 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                pWin (probability of win) is a data-driven methodology that calculates how likely you are to win a specific federal contract. FedBD's composite pWin engine aggregates four weighted signals — AI deep analysis (40%), bid pursuit scorecard (25%), capability matrix (20%), and requirements traceability (15%) — to produce a 0–100 score with actionable recommendations on whether to pursue or pass.
              </div>
            </details>

            <details className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="faq-item-sentinel">
              <summary className="flex items-center justify-between cursor-pointer p-6 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                What is SAM.gov Sentinel?
                <ArrowRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
              </summary>
              <div className="px-6 pt-4 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                SAM.gov Sentinel is FedBD's autonomous opportunity scouting agent. It continuously scans SAM.gov for new federal contract notices, matches them against your NAICS codes, PSC codes, and set-aside designations, and delivers tiered matches (Gold, Silver, Bronze) so you can focus on the highest-value pursuits without manually searching.
              </div>
            </details>

            <details className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="faq-item-go-nogo">
              <summary className="flex items-center justify-between cursor-pointer p-6 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                How does the Go/No-Go bid decision process work?
                <ArrowRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
              </summary>
              <div className="px-6 pt-4 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                FedBD's Go/No-Go process follows federal business development best practices. It includes a 35-question Bid Pursuit Scorecard with AI-powered pre-fill from your documents, a Capability Matrix mapping your team against contract labor categories, a Compliance Matrix for requirement-to-volume traceability, and a composite pWin feedback loop that synthesizes all signals into a single actionable recommendation.
              </div>
            </details>

            <details className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="faq-item-who">
              <summary className="flex items-center justify-between cursor-pointer p-6 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Who is FedBD built for?
                <ArrowRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
              </summary>
              <div className="px-6 pt-4 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                FedBD serves two primary audiences: federal government contractors who need AI-powered bid intelligence to find and win contracts, and independent BD consultants who manage capture pipelines across multiple contractor clients from a single dashboard with white-label capabilities.
              </div>
            </details>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-teal-700 rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">F</span>
              </div>
              <span className="text-sm text-slate-400">© 2026 FedBD. All Rights Reserved.</span>
            </div>
            <div className="text-sm text-slate-500">
              Owned and Operated by <span className="text-teal-400 font-medium">MNGC Group, Inc.</span>
            </div>
            <div className="flex items-center gap-6">
              {!LEGAL_PAGES_HIDDEN && (
                <>
                  <a href="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy-link" aria-label="Privacy policy">Privacy</a>
                  <a href="/terms" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="footer-terms-link" aria-label="Terms of service">Terms</a>
                </>
              )}
              <a href="#contact" className="text-sm text-slate-400 hover:text-white transition-colors" aria-label="Contact us">Contact</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Get Started Modal */}
      <Dialog open={showGetStartedModal} onOpenChange={setShowGetStartedModal}>
        <DialogContent className="sm:max-w-lg" data-testid="get-started-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get Started with FedBD
            </DialogTitle>
            <DialogDescription>
              Choose how you'd like to join the platform
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Option 1: Register as Contractor */}
            <button
              onClick={() => {
                setShowGetStartedModal(false);
                navigate('/pricing?type=contractor');
              }}
              className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group"
              data-testid="register-contractor-option"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                <Building2 className="w-6 h-6 text-teal-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  I'm a Contractor
                </h3>
                <p className="text-sm text-slate-500">
                  Federal contractor looking to discover opportunities, analyze RFPs, and win more contracts with AI.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 mt-1 transition-colors" />
            </button>

            {/* Option 2: Register as Consultant */}
            <button
              onClick={() => {
                setShowGetStartedModal(false);
                navigate('/pricing?type=consultant');
              }}
              className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all text-left group"
              data-testid="register-consultant-option"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                <Briefcase className="w-6 h-6 text-purple-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  I'm an Independent Consultant
                </h3>
                <p className="text-sm text-slate-500">
                  BD consultancy helping multiple contractors find and win federal contracts. Manage all clients from one dashboard.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 mt-1 transition-colors" />
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">or</span>
              </div>
            </div>

            {/* Option 2: Join via Invitation */}
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-6 h-6 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Join an Existing Organization
                  </h3>
                  <p className="text-sm text-slate-500">
                    Enter the invitation code from your email to join your team's workspace.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Enter invitation code (e.g., abc123)"
                    value={inviteToken}
                    onChange={(e) => setInviteToken(e.target.value)}
                    className="pl-10 border-slate-200"
                    data-testid="invite-code-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleInviteSubmit();
                      }
                    }}
                  />
                </div>
                <Button
                  onClick={handleInviteSubmit}
                  disabled={!inviteToken.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="join-with-invite-btn"
                >
                  Join
                </Button>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 text-center">
              Already have an account?{' '}
              <button
                onClick={() => {
                  setShowGetStartedModal(false);
                  navigate('/login');
                }}
                className="text-teal-700 hover:underline font-medium"
                data-testid="modal-sign-in-link"
              >
                Sign in
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
